import React, { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { DndContext, DragEndEvent, DragStartEvent, DragOverEvent, closestCenter, DragOverlay } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSensors, useSensor, PointerSensor, TouchSensor, KeyboardSensor } from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { motion, AnimatePresence } from 'framer-motion';
import { AppItem } from '@/hooks/app-library/use-app-favorites';
import { useDragContext } from '@/contexts/drag/DragContext';
import { GRID_BREAKPOINTS } from '@/config/grid-breakpoints';
import PinnedAppSlot from './PinnedAppSlot';
import PinnedAppGhost from './PinnedAppGhost';

interface PinnedAppsDnDProps {
  pinnedApps: (AppItem | null)[];
  onPositionSwap: (fromPos: 1 | 2 | 3 | 4, toPos: 1 | 2 | 3 | 4) => void;
}

const PinnedAppsDnD: React.FC<PinnedAppsDnDProps> = ({ pinnedApps, onPositionSwap }) => {
  const [activeItem, setActiveItem] = useState<AppItem | null>(null);
  const [currentDropTarget, setCurrentDropTarget] = useState<1 | 2 | 3 | 4 | null>(null);
  const [originalPosition, setOriginalPosition] = useState<1 | 2 | 3 | 4 | null>(null);
  const { setIsDragging } = useDragContext();
  
  // Mobile-optimized sensors inlined
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const getPositionFromId = (id: string): 1 | 2 | 3 | 4 | null => {
    const match = id.match(/pinned-(\d+)/);
    return match ? (parseInt(match[1]) as 1 | 2 | 3 | 4) : null;
  };

  const getPositionFromDropId = (id: string): 1 | 2 | 3 | 4 | null => {
    const match = id.match(/position-(\d+)/);
    return match ? (parseInt(match[1]) as 1 | 2 | 3 | 4) : null;
  };

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setIsDragging(true);
    
    const { active } = event;
    const position = getPositionFromId(active.id as string);
    
    if (position && active.data.current) {
      const { app } = active.data.current;
      setActiveItem(app);
      setOriginalPosition(position);
    }
  }, [setIsDragging]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setIsDragging(false);
    
    const { active, over } = event;
    setActiveItem(null);
    setCurrentDropTarget(null);
    setOriginalPosition(null);

    if (!over || active.id === over.id) {
      return;
    }

    const fromPos = getPositionFromId(active.id as string);
    let toPos: 1 | 2 | 3 | 4 | null = null;

    // Handle dropping on another draggable item
    if (over.id.toString().startsWith('pinned-')) {
      toPos = getPositionFromId(over.id as string);
    }
    // Handle dropping on a position slot
    else if (over.id.toString().startsWith('position-')) {
      toPos = getPositionFromDropId(over.id as string);
    }

    if (fromPos && toPos && fromPos !== toPos) {
      // Add a small delay to allow Framer Motion layout animations to start
      setTimeout(() => {
        onPositionSwap(fromPos, toPos);
      }, 100);
    }
  }, [onPositionSwap, setIsDragging]);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { over } = event;
    if (over && over.id) {
      let targetPosition: 1 | 2 | 3 | 4 | null = null;
      
      // Handle hovering over another draggable item
      if (over.id.toString().startsWith('pinned-')) {
        targetPosition = getPositionFromId(over.id as string);
      }
      // Handle hovering over a position slot
      else if (over.id.toString().startsWith('position-')) {
        targetPosition = getPositionFromDropId(over.id as string);
      }
      
      if (targetPosition) {
        setCurrentDropTarget(targetPosition);
      }
    } else {
      // Not hovering over any valid drop target
      setCurrentDropTarget(null);
    }
  }, []);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
    >
      <div className={`grid ${GRID_BREAKPOINTS.appLibraryPinnedAppsRow} gap-2 md:gap-6`}>
        {[1, 2, 3, 4].map((position) => (
          <PinnedAppSlot
            key={`pinned-slot-${position}`}
            position={position as 1 | 2 | 3 | 4}
            app={pinnedApps[position - 1]}
          />
        ))}
      </div>

      {/* Simple portal DragOverlay */}
      {typeof window !== 'undefined' && createPortal(
        <DragOverlay
          dropAnimation={null}
          modifiers={[]}
          zIndex={1000}
          style={{
            pointerEvents: 'none',
            willChange: 'transform',
            userSelect: 'none',
            touchAction: 'none',
          }}
        >
          {activeItem ? <PinnedAppGhost app={activeItem} currentDropTarget={currentDropTarget} originalPosition={originalPosition} /> : null}
        </DragOverlay>,
        document.body
      )}
    </DndContext>
  );
};

export default PinnedAppsDnD; 